import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import apiInstance from '../../api/api';
import {showNotification} from "../notification/notificationSlice";
import {handleAxiosError} from "../../utils/axiosErrorHandler";
import {ApiFailResponse} from "../../utils/interfaces/apiTypes";
import apiEndpoints from '../../utils/api/endpoints';

export interface LocationCampaignStateProps {
    loading: boolean;
    success: boolean;
    verifyLoading: boolean;
    currentCampaign: any;
    error: string | null;
}

const initialState: LocationCampaignStateProps = {
    loading: false,
    success: false,
    verifyLoading: false,
    currentCampaign: null,
    error: null,
}

// Verifică campania și locația
export const verifyLocationCampaign = createAsyncThunk(
    'locationCampaign/verifyLocationCampaign',
    async (payload: { location_code: string; campaign_uuid?: string }, thunkAPI) => {
        try {
            const response = await apiInstance.post(apiEndpoints.verifyLocationCampaign, payload);
            
            return response.data;
        } catch (error) {
            const errorData = handleAxiosError(error) as ApiFailResponse;
            
            thunkAPI.dispatch(showNotification({
                message: errorData.message || 'Eroare la verificarea campaniei', 
                variant: 'error'
            }));

            return thunkAPI.rejectWithValue(errorData);
        }
    }
);

// Înregistrează participantul
export const insertLocationCampaign = createAsyncThunk(
    'locationCampaign/insertLocationCampaign',
    async (payload: FormData, thunkAPI) => {
        try {
            const response = await apiInstance.post(apiEndpoints.locationCampaign, payload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            
            thunkAPI.dispatch(showNotification({
                message: response.data.message || 'Înregistrare procesată cu succes!', 
                variant: 'success'
            }));

            return response.data;
        } catch (error) {
            const errorData = handleAxiosError(error) as ApiFailResponse;
            
            thunkAPI.dispatch(showNotification({
                message: errorData.message || 'Eroare la înregistrare', 
                variant: 'error'
            }));

            return thunkAPI.rejectWithValue(errorData);
        }
    }
);

const locationCampaignSlice = createSlice({
    name: "locationCampaign",
    initialState,
    reducers: {
        resetLocationCampaignState: (state) => {
            state.loading = false;
            state.success = false;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearCampaign: (state) => {
            state.currentCampaign = null;
        }
    },
    extraReducers: builder => {
        builder
            // Verify location campaign
            .addCase(verifyLocationCampaign.pending, (state) => {
                state.verifyLoading = true;
                state.error = null;
            })
            .addCase(verifyLocationCampaign.fulfilled, (state, action) => {
                state.verifyLoading = false;
                state.currentCampaign = action.payload.data;
                state.error = null;
            })
            .addCase(verifyLocationCampaign.rejected, (state, action) => {
                state.verifyLoading = false;
                state.currentCampaign = null;
                state.error = (action.payload as ApiFailResponse)?.message || 'Eroare la verificarea campaniei';
            })
            // Insert location campaign
            .addCase(insertLocationCampaign.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(insertLocationCampaign.fulfilled, (state) => {
                state.loading = false;
                state.success = true;
                state.error = null;
            })
            .addCase(insertLocationCampaign.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = (action.payload as ApiFailResponse)?.message || 'Eroare la înregistrare';
            });
    }
});

export const { 
    resetLocationCampaignState, 
    clearError, 
    clearCampaign 
} = locationCampaignSlice.actions;

export default locationCampaignSlice.reducer;